import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, of } from "rxjs";
import { catchError, map, shareReplay } from "rxjs/operators";
import { environment } from "src/environments/environment";

/** Yearly unit prices in cents for the upgrade dialog's plan cards. */
export interface YearlyPlanPricing {
  starter?: number;
  growth?: number;
  salesPro?: number;
}

/**
 * Caches the public plan-pricing lookup used by the upgrade dialog.
 *
 * Deliberately uses HttpClient directly (not DataService): DataService
 * imports WarningDialogComponent for its 403 upsell handling, and the
 * dialog needs pricing — going through DataService here would create the
 * circular import chain WarningDialog → pricing → DataService → WarningDialog.
 *
 * Fetched once per session and replayed — the upgrade dialog opens dozens
 * of times and previously refetched on every open. A failed fetch drops
 * the cache so the next open retries.
 */
@Injectable({ providedIn: "root" })
export class PlanPricingService {
  private yearlyPlanPricing$: Observable<YearlyPlanPricing> | null = null;

  constructor(private readonly http: HttpClient) {}

  getYearlyPlanPricing(): Observable<YearlyPlanPricing> {
    if (!this.yearlyPlanPricing$) {
      this.yearlyPlanPricing$ = this.http.get(environment.API_URL + "v1/subscriptions/plans").pipe(
        map((response: any) => {
          const plans = response?.result ?? [];
          const unitAmount = (identifier: string): number | undefined =>
            plans.find((plan: any) => plan.identifier == identifier)?.unit_amount;
          return {
            starter: unitAmount("starter-yearly"),
            growth: unitAmount("growth-yearly"),
            salesPro: unitAmount("sales-pro-yearly"),
          };
        }),
        catchError(() => {
          this.yearlyPlanPricing$ = null;
          return of<YearlyPlanPricing>({});
        }),
        shareReplay(1)
      );
    }
    return this.yearlyPlanPricing$;
  }
}
